const deferredItems = {};

const loadStyle = (item) => {
    let href = item.css;

    if (href) {
        href = Array.isArray(href) ? href : [href];
        item.css = {
            href,
            promise: Promise.all(
                href.map(
                    (href) =>
                        new Promise((resolve, reject) => {
                            window.addStylesheet(href, (event) => {
                                resolve(event?.target);
                            });
                        })
                )
            ),
        };

        return item.css.promise;
    }
};

const getCustomElementName = (module) => {
    const { customElementName } = module;

    if (typeof customElementName !== "undefined") {
        return customElementName;
    }

    let moduleClass = Object.values(module)[0];
    if (moduleClass?.name && (moduleClass + "").indexOf("class ") === 0) {
        let name = moduleClass.name;

        // CamelCase to dashes
        name = name.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
        // remove starting dash
        if (name.indexOf("-") === 0) name = name.substring(1);

        return name;
    }

    return false;
};

const load = (name, loaderTarget, loaderPosition = "beforeend") => {
    const _load = (name) => {
        const item = deferredItems[name];

        item?.depends?.forEach?.(load);
        item.promise = Promise.all([
            item.src && import(window._resolveImport?.(item.src) || item.src),
            loadStyle(item),
        ]);
        item.loaded = true;
    };

    if (deferredItems[name]?.loaded) return false; // already loaded

    (async () => {
        if (loaderTarget && loaderPosition) {
            loaderTarget.dispatchEvent(
                new CustomEvent("loading-overlay-show", {
                    bubbles: true,
                    detail: {
                        position: loaderPosition,
                    },
                })
            );
        }

        _load(name);

        let [module] = await deferredItems[name].promise;
        if (module) {
            let elementName = getCustomElementName(module);

            if (elementName) {
                if (!customElements.get(elementName)) {
                    customElements.define(
                        elementName,
                        Object.values(module)[0]
                    );
                }

                await customElements.whenDefined(elementName);
            }
        }

        loaderTarget &&
            loaderPosition &&
            loaderTarget.dispatchEvent(
                new CustomEvent("loading-overlay-hide", { bubbles: true })
            );
    })();

    return true; // loading started
};

const processReveal = (name, settings) => {
    const { reveal } = settings;

    if (!reveal) return;

    if (reveal === "body") {
        // load script instantly when reveal is body
        // avoid creating unnecessary observers
        load(name);

        return false;
    } else {
        let revealObserver = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                load(name);
                revealObserver.disconnect();
            }
        });

        document.querySelectorAll(reveal).forEach((el) => {
            el.observedBy = el.observedBy || [];
            if (!el.observedBy.includes(name)) {
                revealObserver.observe(el);
                el.observedBy.push(name);
            }
        });
    }
};

const processEvent = (name, settings) => {
    const eventName = (settings.event || "").split("::")[0];

    if (!eventName) return;

    const selector = settings.event.replace(`${eventName}::`, "");
    const _handler = (event) => {
        const { target } = event;
        const ctx = target.closest ? target.closest(selector) : target;

        let __iteration = 1;

        const __dispatchEvent = async () => {
            const module = await import(
                window._resolveImport?.(settings.src) || settings.src
            );
            let customElementName;

            try {
                customElementName = getCustomElementName(module);
            } catch (error) {
                // (tested 3/26/2024)
                // Exception here occurs only for Safari browsers
                // when module has top lever import with await.
                // This recurcive call of __disaptchEvent can remove
                // when you start to use importmap
                // and 'import from ..' instead of 'await import(...)'
                if (__iteration < 20) {
                    setTimeout(__dispatchEvent, 200);
                    __iteration++;
                } else {
                    // Prevent infinit recursion.
                    console.log(
                        `We waited ${__iteration * 0.2}s for ${
                            settings.src
                        } . It's too much. Skip it.`
                    );
                }

                return;
            }

            if (customElementName) {
                await customElements.whenDefined(customElementName);
            }

            target.dispatchEvent(event);
        };

        if (!ctx) return;

        event.stopPropagation();
        event.preventDefault();

        let loaderTarget = ctx.tagName && ctx;
        if (ctx.tagName === "FORM") {
            loaderTarget = ctx.add || ctx;
        }

        document.removeEventListener(eventName, _handler);
        if (load(name, loaderTarget)) {
            // component loading started
            // wait for it and dispatch event
            settings.src && __dispatchEvent();
        }
    };

    document.listensFor = document.listensFor || [];
    if (!document.listensFor.includes(name)) {
        document.addEventListener(eventName, _handler);
        document.listensFor.push(name);
    }
};

const initialize = () => {
    document
        .querySelectorAll('script[type="text/init-deferred"]')
        .forEach((script) => {
            const newDeferred = JSON.parse(script.textContent);

            Object.entries(newDeferred).forEach(([key, item]) => {
                if (!deferredItems[key]) {
                    deferredItems[key] = item;
                }
            });
            script.remove();
        });
    Object.entries(deferredItems).forEach(([name, settings]) => {
        if (!settings.loaded) {
            processReveal(name, settings);
            processEvent(name, settings);
        }
    });
};

// Some deferred components have popstate listeners.
// Add to state object name of the deferred component.
// And global listener below loads it once popstate event triggered.
window.addEventListener("popstate", (event) => {
    const name = event.state?.deferredName;

    if (name && load(name)) {
        // load returns `true` when deferred hasn't been
        // loaded before and loading started now
        let el = document.getElementById(event.state.id);
        if (el)
            el.afterLoadCallback = function () {
                this.onPopstate?.(event);
                delete this.afterLoadCallback;
            };
    }
});

// instantly initialize defered component
initialize();

export { initialize, load, deferredItems };
